/**
 * Invitation Service - API methods for team member invitations
 */

import apiClient from './apiClient';

export interface InvitationRole {
  id: string;
  name: string;
}

export interface Invitation {
  id: string;
  email: string;
  name?: string | null;
  status: 'pending' | 'accepted' | 'revoked' | 'expired';
  roles?: InvitationRole[];
  expiresAt: string;
  acceptedAt?: string;
  revokedAt?: string;
  createdAt: string;
  createdByName?: string;
  inviteLink?: string;
}

export interface SendInvitationRequest {
  email: string;
  name?: string;
  roleId: string;
  supervisorId?: string;
  groupIds?: string[];
}

export interface InvitationsResponse {
  pending: Invitation[];
  accepted: Invitation[];
}

/**
 * Send an invitation to a new team member
 */
export const sendInvitation = async (data: SendInvitationRequest): Promise<Invitation> => {
  const response = await apiClient.post<{ success: boolean; data: Invitation }>(
    '/api/v1/invitations',
    data,
  );
  return response.data.data;
};

/**
 * Get all invitations for the organization (pending + accepted sections)
 */
export const getInvitations = async (): Promise<InvitationsResponse> => {
  const response = await apiClient.get<{ success: boolean; data: InvitationsResponse }>(
    '/api/v1/invitations',
  );
  return response.data.data;
};

/**
 * Resend an invitation (extends expiry, returns refreshed invite link)
 */
export const resendInvitation = async (id: string): Promise<Invitation & { inviteLink: string }> => {
  const response = await apiClient.post<{ success: boolean; data: { inviteLink: string }; message: string }>(
    `/api/v1/invitations/${id}/resend`,
  );
  return { id, inviteLink: response.data.data.inviteLink } as Invitation & { inviteLink: string };
};

/**
 * Revoke a pending invitation
 */
export const revokeInvitation = async (id: string): Promise<Invitation> => {
  const response = await apiClient.post<{ success: boolean; data: Invitation }>(
    `/api/v1/invitations/${id}/revoke`,
  );
  return response.data.data;
};

// ============================================================================
// Bulk Invite Types & Methods
// ============================================================================

export interface BulkValidateRow {
  row: number;
  email: string;
  name?: string;
  role?: string;
  roleId?: string;
  valid: boolean;
  reason?: string;
}

export interface BulkValidateResponse {
  rows: BulkValidateRow[];
  validCount: number;
  invalidCount: number;
  totalCount: number;
}

export interface BulkExecuteResponse {
  created: number;
  skipped: number;
}

interface BulkValidateApiRow {
  rowNumber: number;
  email: string;
  name?: string;
  roleName?: string;
  roleId?: string;
  status: 'valid' | 'invalid';
  reason?: string;
}

interface BulkValidateApiResponse {
  valid: BulkValidateApiRow[];
  invalid: BulkValidateApiRow[];
  validCount: number;
  invalidCount: number;
  totalRows: number;
}

const mapBulkInviteRow = (row: BulkValidateApiRow): BulkValidateRow => ({
  row: row.rowNumber,
  email: row.email,
  name: row.name,
  role: row.roleName,
  roleId: row.roleId,
  valid: row.status === 'valid',
  reason: row.reason,
});

/**
 * Validate a bulk invite CSV file. Returns per-row validation results.
 */
export const bulkValidate = async (file: File): Promise<BulkValidateResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await apiClient.post<{ success: boolean; data: BulkValidateApiResponse }>(
    '/api/v1/invitations/bulk/validate',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  const rows = [...response.data.data.valid, ...response.data.data.invalid]
    .sort((a, b) => a.rowNumber - b.rowNumber)
    .map(mapBulkInviteRow);

  return {
    rows,
    validCount: response.data.data.validCount,
    invalidCount: response.data.data.invalidCount,
    totalCount: response.data.data.totalRows,
  };
};

/**
 * Execute bulk invite for pre-validated rows
 */
export const bulkExecute = async (
  rows: { email: string; name?: string; roleId?: string }[],
): Promise<BulkExecuteResponse> => {
  const response = await apiClient.post<{ success: boolean; data: BulkExecuteResponse }>(
    '/api/v1/invitations/bulk/execute',
    { rows },
  );
  return response.data.data;
};

export type InvitationDetails = Pick<Invitation, 'email' | 'name'> & {
  name: string | null;
  organizationName: string;
};

/**
 * Fetch invitation metadata by token (public — no auth required)
 * Used to pre-populate the accept form with the invitee's name.
 */
export const getInvitationByToken = async (token: string): Promise<InvitationDetails> => {
  const response = await apiClient.get<{ success: boolean; data: InvitationDetails }>(
    `/api/v1/invitations/by-token/${token}`,
  );
  return response.data.data;
};

export interface AcceptInvitationRequest {
  token: string;
  firstName: string;
  lastName: string;
  password: string;
}

/**
 * Accept an invitation (public — no auth required)
 * Creates user account and org membership
 */
export const acceptInvitation = async (
  data: AcceptInvitationRequest,
): Promise<{ message: string; memberId: string }> => {
  const response = await apiClient.post<{
    success: boolean;
    data: { memberId: string };
    message: string;
  }>('/api/v1/invitations/accept', data);
  return { message: response.data.message, memberId: response.data.data.memberId };
};

const invitationService = {
  sendInvitation,
  getInvitations,
  resendInvitation,
  revokeInvitation,
  getInvitationByToken,
  acceptInvitation,
  bulkValidate,
  bulkExecute,
};

export default invitationService;
